import React, { useState } from "react";
import Avatar from "@material-ui/core/Avatar";
import { Button } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import LockIcon from "@mui/icons-material/Lock";
import ResetPassword from "./ResetPassword";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";

import { useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  resendVerificationCode,
  forgotPassword,
} from "../../../../service/endpoint-manager";
import { useSnackbar } from "notistack";
import Loading from "../../Shared/Loading";
import ButtonTextLoading from "../../Shared/ButtonTextLoading";
import { useNavigate } from "react-router-dom";
import IconButton from "@material-ui/core/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: "#3f51b5",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },

  submit: {
    margin: theme.spacing(3, 0, 2),
  },

  header: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  },

  backButtonStyles: {
    width: "20%",
  },

  pageHeading: {
    width: "80%",
    textAlign: "left",
    paddingLeft: "1.5rem",
  },
}));

export default function ForgotPasswordInputEmail() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const [userName, setUserName] = useState(false);

  const navigate = useNavigate();

  const handleCheckEmailAndSendVerificationCode = async (email) => {
    setUserName(email);
    const result = await forgotPassword(email);

    setVerificationCodeSent(true);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    onSubmit: async function (values) {
      setIsSubmitting(true);
      let result = null;

      // show a success message in case of a successful response from the API (verification successful)
      if (
        result?.data?.body?.statusCode === 200 &&
        result?.data?.body?.response === "SUCCESS"
      ) {
        enqueueSnackbar("Email Verification successful", {
          variant: "success",
        });
        navigate("/");
      } else {
        // else show an error message
        enqueueSnackbar("Something went wrong. Please try again", {
          variant: "error",
        });
      }

      setIsSubmitting(false);
    },

    // validation schema for formik using yup
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email")
        .required("Email is required"),
    }),
  });

  const handleResendVerificationCode = async (e) => {
    e.preventDefault();
    const data = location?.state?.username
      ? location?.state?.username
      : formik.values.username;

    // call the resendVerificationCode API with the specified values
    const result = await resendVerificationCode(data);
    if (result.status === 200) {
      // show a success message in case of a successful response from the API (verification code resent)
      enqueueSnackbar("Verification code sent", {
        variant: "success",
      });
    } else {
      // else show an error message
      enqueueSnackbar("Something went wrong. Please try again", {
        variant: "error",
      });
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      {!verificationCodeSent && (
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <LockIcon />
          </Avatar>
          <section className={classes.header}>
            <IconButton
              className={classes.backButtonStyles}
              onClick={() => {
                navigate("/auth/login");
              }}
            >
              <ArrowBackIcon color="#000" />
            </IconButton>
            <Typography
              component="h1"
              variant="h5"
              className={classes.pageHeading}
            >
              Forgot your password?
            </Typography>
          </section>
          <form
            onSubmit={formik.handleSubmit}
            className={classes.form}
            noValidate
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  error={formik.touched.email && formik.errors.email}
                  required
                  fullWidth
                  id="email"
                  label="Please enter your email"
                  name="email"
                  autoComplete="email"
                  variant="outlined"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {
                  <div
                    style={{
                      color: "red",
                      fontSize: "0.6rem",
                      visibility:
                        formik.touched.email && formik.errors.email
                          ? "visible"
                          : "hidden",
                    }}
                  >
                    {formik.errors.email || "Email is required"}
                  </div>
                }
              </Grid>
            </Grid>

            {/* submit button */}
            <Button
              type="button"
              // button will be disabled if form has errors
              disabled={
                Object.keys(formik.errors).length > 0 || !formik.values.email
              }
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              onClick={(e) => {
                handleCheckEmailAndSendVerificationCode(formik.values.email);
              }}
            >
              <ButtonTextLoading isSubmitting={isSubmitting} body={"Next"} />
            </Button>
          </form>
        </div>
      )}

      {verificationCodeSent && (
        <ResetPassword
          setVerificationCodeSent={setVerificationCodeSent}
          userName={userName}
        />
      )}
    </Container>
  );
}
